import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";
import { OrderShipments } from "#/components/order-status/OrderShipments";
import { PaymentInstructions } from "#/components/order-status/PaymentInstructions";
import { StatusTracker } from "#/components/order-status/StatusTracker";
import { formatMoney } from "#/lib/medusa-types/commerce";
import { notify } from "#/lib/notify";
import { ROUTES } from "#/lib/routes";
import { useSession } from "#/lib/useSession";
import { orderQueries } from "#/queries/orders";
import { selectLastOrderId, useCartStore } from "#/store/cart";

function grantOrderAccess(orderId: string) {
  useCartStore.getState().setLastOrderId(orderId);
}

const searchSchema = z.object({
  paymongo_status: z.enum(["pending", "success", "failed"]).optional(),
});

type AccessCheck = {
  order: { id: string; customer_id?: string | null };
  customerId: string | null;
  lastOrderId: string | null;
};

export function isOrderAccessibleToViewer({
  order,
  customerId,
  lastOrderId,
}: AccessCheck): boolean {
  if (customerId && order.customer_id && order.customer_id === customerId) {
    return true;
  }
  if (lastOrderId && lastOrderId === order.id) return true;
  return false;
}

export const Route = createFileRoute("/order/$id")({
  head: () => ({ meta: [{ title: "Order Status" }] }),
  validateSearch: searchSchema,
  loader: async ({ context: { queryClient }, params }) => {
    try {
      await queryClient.ensureQueryData(orderQueries.detail(params.id));
    } catch {
      // Bad/expired id — fall through to the component's not-found branch.
    }
  },
  component: OrderStatusPage,
});

function OrderStatusPage() {
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { data: order, isPending } = useQuery(orderQueries.detail(id));
  const { customer } = useSession();
  const lastOrderId = useCartStore(selectLastOrderId);

  useEffect(() => {
    if (!search.paymongo_status) return;
    if (search.paymongo_status === "success") {
      grantOrderAccess(id);
      notify.success("Payment confirmed");
    } else if (search.paymongo_status === "failed") {
      notify.error("Payment failed");
    } else {
      notify.info("Payment is processing");
    }
    navigate({
      to: "/order/$id",
      params: { id },
      search: {},
      replace: true,
    });
  }, [search.paymongo_status, id, navigate]);

  if (isPending) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-16">
        <div className="h-10 w-72 animate-pulse rounded-full bg-lp-green-soft" />
        <div className="mt-8 h-96 animate-pulse rounded-3xl bg-lp-green-soft" />
      </section>
    );
  }

  const accessGranted = order
    ? search.paymongo_status === "success" ||
      isOrderAccessibleToViewer({
        order,
        customerId: customer?.id ?? null,
        lastOrderId,
      })
    : false;

  if (!order || !accessGranted) {
    return (
      <section className="mx-auto max-w-3xl px-6 py-16">
        <div className="rounded-3xl bg-lp-green-soft p-10 text-center font-display text-lp-text">
          <div className="mb-2 font-bold text-lp-green-dk text-xl">
            Order not found
          </div>
          <p className="m-0 mb-6">
            We couldn't find that order. If you just placed it and got
            redirected here from a payment provider, give it a moment and
            refresh.
          </p>
          <Link
            to={ROUTES.shop}
            className="font-display font-semibold text-lp-green-dk underline underline-offset-4 hover:text-lp-green-mid"
          >
            Back to shop
          </Link>
        </div>
      </section>
    );
  }

  const orderTotal = Number(order.total ?? 0);
  const feeRaw = order.metadata?.convenience_fee;
  const feeNum = typeof feeRaw === "number" ? feeRaw : Number(feeRaw);
  const convenienceFee = Number.isFinite(feeNum) && feeNum > 0 ? feeNum : 0;

  const createdAt = order.created_at
    ? new Date(order.created_at).getTime()
    : Date.now();
  const pollExpired =
    order.payment_status === "requires_action" &&
    Date.now() - createdAt > 60_000;

  return (
    <section className="mx-auto max-w-3xl px-6 py-16">
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="m-0 font-bold font-display text-3xl text-lp-green-dk">
          Order #{order.display_id ?? order.id.slice(0, 8)}
        </h1>
        <span className="font-mono text-lp-text-lt text-sm capitalize">
          {order.status}
        </span>
      </div>
      {convenienceFee > 0 ? (
        <div className="mt-2 font-display text-lp-text">
          <p className="m-0 text-lp-text-lt text-sm">
            Order total{" "}
            <span className="font-mono">
              {formatMoney(orderTotal, order.currency_code)}
            </span>
            {"  ·  "}Convenience fee{" "}
            <span className="font-mono">
              {formatMoney(convenienceFee, order.currency_code)}
            </span>
          </p>
          <p className="m-0">
            Total charged{" "}
            <span className="font-bold font-mono">
              {formatMoney(orderTotal + convenienceFee, order.currency_code)}
            </span>
          </p>
        </div>
      ) : (
        <p className="mt-2 font-display text-lp-text">
          Total{" "}
          <span className="font-mono">
            {formatMoney(orderTotal, order.currency_code)}
          </span>
        </p>
      )}

      <div className="mt-10 rounded-3xl bg-lp-green-soft p-10">
        <StatusTracker
          status={order.status}
          payment_status={order.payment_status}
          fulfillment_status={order.fulfillment_status}
        />
      </div>

      {pollExpired && (
        <div className="mt-6 rounded-2xl bg-lp-yellow/30 p-6 font-display text-lp-text">
          Still processing. We'll email you the moment it clears — feel free to
          close this tab.
        </div>
      )}

      <div className="mt-6">
        <PaymentInstructions order={order} />
      </div>

      <div className="mt-6">
        <OrderShipments order={order} />
      </div>

      <div className="mt-12 flex flex-wrap gap-4">
        <Link
          to={ROUTES.shop}
          className="cursor-pointer rounded-full border-2 border-lp-green-mid bg-white px-6 py-3 font-display font-semibold text-lp-green-dk hover:bg-lp-green-soft"
        >
          Continue browsing
        </Link>
        <Link
          to={ROUTES.track}
          className="cursor-pointer rounded-full border-2 border-lp-green-mid bg-white px-6 py-3 font-display font-semibold text-lp-green-dk hover:bg-lp-green-soft"
        >
          Track my order
        </Link>
        <Link
          to={ROUTES.accountOrders}
          className="cursor-pointer rounded-full border-0 bg-lp-green-mid px-6 py-3 font-display font-semibold text-white hover:bg-lp-green-dk"
        >
          My orders
        </Link>
      </div>
    </section>
  );
}
